import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState } from 'react';
import {
  Text, Spinner, Badge, Button, Field, Input, Dropdown, Option,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { fetchHarvestSummary, createHarvestLog, type PackedBoxInput, type CreateHarvestLogInput } from '../api/harvest.api';
import { useEntityNames } from '../hooks/useEntityNames';
import { CycleCombobox } from '../components/shared/CycleCombobox';
import { useRanchoLotes } from '../hooks/useRanchoLotes';
import { fetchModules } from '../api/modules.api';
import { parseLoteZoneId } from '../utils/lote-zone-options';
import { useLoteZoneOptions } from '../hooks/useLoteZoneOptions';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { fetchMermaSummary, type MermaReason } from '../api/merma.api';
import type { HarvestKpi } from '../types/api.types';
import { KpiCard } from '../components/shared/KpiCard';
import { useAuthStore } from '../store/auth.store';
import { useRanchoStore } from '../store/rancho.store';
import { TelegramHelpButton } from '../components/shared/TelegramHelpButton';
import { useT } from '../i18n';
import { NumberInput } from '../components/shared/NumberInput';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { PageTitle } from '../components/shared/PageTitle';
import { DataTable } from '../components/shared/DataTable';

interface HarvestRow {
  harvestId: string; harvestedAt: string; loteId: string; cycleNumber: number;
  kgTotal: number; gradeA?: number; packoutPct?: number; costPerKg?: number; recordedBy: string; recordedByName?: string | null;
}

const useStyles = makeStyles({
  root:   { display: 'flex', flexDirection: 'column', gap: '24px' },
  kpiRow: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  grid:   {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '24px',
    '@media (max-width: 768px)': { gridTemplateColumns: '1fr' },
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius:    tokens.borderRadiusMedium,
    padding:         '20px',
    display:         'flex',
    flexDirection:   'column',
    gap:             '12px',
    overflowX:       'auto',
  },
  sectionHeader: {
    display:        'flex',
    alignItems:     'center',
    gap:            '8px',
    paddingTop:     '8px',
    borderTop:      `1px solid ${tokens.colorNeutralStroke1}`,
    marginTop:      '8px',
  },
});

// Merma reason → i18n key mapping
const REASON_KEYS: Record<MermaReason, string> = {
  CLIMA:            'mermaReasonCLIMA',
  PLAGA:            'mermaReasonPLAGA',
  MECANICO:         'mermaReasonMECANICO',
  RECHAZO_CALIDAD:  'mermaReasonRECHAZO_CALIDAD',
  PUDRICION:        'mermaReasonPUDRICION',
  OTRO:             'mermaReasonOTRO',
};

// Merma status → i18n key mapping
const STATUS_KEYS: Record<string, string> = {
  REGISTRADA: 'statusREGISTRADA',
  VERIFICADA: 'statusVERIFICADA',
  CERRADA:    'statusCERRADA',
};

const EMPTY_BOX: PackedBoxInput = { calibre: '', boxCount: 0, weightKg: 0, grade: 'A' };

function NewHarvestDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t  = useT();
  const h  = t.harvest;
  const qc = useQueryClient();
  // Selección del árbol lote > zona > subzona ('' = ninguna).
  const [zoneId, setZoneId] = useState('');
  const activeRanchoId = useRanchoStore((st) => st.activeRanchoId);
  const { data: modules = [] } = useQuery({
    queryKey: ['modules-rancho', activeRanchoId],
    queryFn:  () => fetchModules({ ranchoId: activeRanchoId }),
    enabled:  open,
  });
  const sel = zoneId ? parseLoteZoneId(zoneId, modules) : null;
  const loteId = sel?.loteId ?? '';
  const moduleId = sel?.moduleId;

  const { lotes } = useRanchoLotes(open, { value: loteId, reset: () => setZoneId('') }, undefined, true);
  const zoneOptions = useLoteZoneOptions(lotes, modules);
  const [batchId, setBatchId] = useState('');
  const [containerCode, setContainerCode] = useState('');
  const [notes, setNotes] = useState('');
  const [boxes, setBoxes] = useState<PackedBoxInput[]>([{ ...EMPTY_BOX }]);
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  const reset = () => {
    setZoneId(''); setBatchId(''); setContainerCode(''); setNotes(''); setBoxes([{ ...EMPTY_BOX }]); setError(''); fe.clear();
  };

  const mut = useMutation({
    mutationFn: () => {
      const input: CreateHarvestLogInput = {
        batchId: batchId.trim(),
        ...(moduleId ? { moduleId } : {}),
        containerCode: containerCode.trim() || undefined,
        notes: notes.trim() || undefined,
        boxes: boxes.filter(b => b.calibre.trim() && b.boxCount > 0),
      };
      return createHarvestLog(loteId, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['harvestSummary'] });
      reset();
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? h.createError),
  });

  const updateBox = (idx: number, patch: Partial<PackedBoxInput>) =>
    setBoxes(bs => bs.map((b, i) => i === idx ? { ...b, ...patch } : b));

  const handleSubmit = () => {
    setError('');
    const validBoxes = boxes.filter(b => b.calibre.trim() && b.boxCount > 0);
    const ok = fe.validate({
      loteId: !loteId && h.loteRequired,
      batchId: !batchId.trim() && h.batchRequired,
      boxes: validBoxes.length === 0 && h.boxesRequired,
    });
    if (!ok) return;
    mut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) { reset(); onClose(); } }}>
      <DialogSurface style={{ maxWidth: '560px' }}>
        <DialogBody>
          <DialogTitle action={
            <TelegramHelpButton
              topic="harvest"
              voiceExample={t.telegramHelp.harvestVoice}
              voiceDesc={t.telegramHelp.harvestDesc}
            />
          }>{h.newHarvestTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={h.fieldLoteZone} {...fe.props('loteId')}>
                  <SearchableSelect
                    placeholder={t.common.searchLotePlaceholder}
                    options={zoneOptions}
                    value={zoneId}
                    onChange={(id) => { setZoneId(id); setBatchId(''); fe.clear('loteId'); }}
                    disabled={mut.isPending}
                    noResultsText={t.common.searchNoResults}
                  />
                </Field>
                <Field label={h.fieldBatchId} {...fe.props('batchId')}>
                  <CycleCombobox loteId={loteId} value={batchId} onChange={(v) => { setBatchId(v); fe.clear('batchId'); }} disabled={mut.isPending} />
                </Field>
              </div>

              <Field label={h.fieldContainerCode}>
                <Input value={containerCode} onChange={(_, d) => setContainerCode(d.value)} disabled={mut.isPending} />
              </Field>

              <Text weight="semibold" size={200}>{h.fieldBoxesTitle}</Text>
              {boxes.map((box, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto', gap: '8px', alignItems: 'end' }}>
                  <Field label={h.fieldCalibre} {...(idx === 0 ? fe.props('boxes') : {})}>
                    <Input value={box.calibre} onChange={(_, d) => { updateBox(idx, { calibre: d.value }); fe.clear('boxes'); }} disabled={mut.isPending} />
                  </Field>
                  <Field label={h.fieldBoxCount}>
                    <NumberInput value={String(box.boxCount || '')} onValueChange={(v) => { updateBox(idx, { boxCount: parseFloat(v) || 0 }); fe.clear('boxes'); }} disabled={mut.isPending} />
                  </Field>
                  <Field label={h.fieldWeightKg}>
                    <NumberInput value={String(box.weightKg || '')} onValueChange={(v) => updateBox(idx, { weightKg: parseFloat(v) || 0 })} disabled={mut.isPending} />
                  </Field>
                  <Field label={h.fieldGrade}>
                    <Dropdown
                      selectedOptions={[box.grade]}
                      value={box.grade}
                      onOptionSelect={(_, d) => updateBox(idx, { grade: d.optionValue as 'A' | 'B' | 'C' })}
                      disabled={mut.isPending}
                    >
                      <Option value="A">A</Option>
                      <Option value="B">B</Option>
                      <Option value="C">C</Option>
                    </Dropdown>
                  </Field>
                  <Button
                    size="small"
                    appearance="subtle"
                    disabled={mut.isPending || boxes.length === 1}
                    onClick={() => setBoxes(bs => bs.filter((_, i) => i !== idx))}
                  >
                    {h.removeBoxRow}
                  </Button>
                </div>
              ))}
              <Button size="small" onClick={() => setBoxes(bs => [...bs, { ...EMPTY_BOX }])} disabled={mut.isPending}>
                {h.addBoxRow}
              </Button>

              <Field label={h.fieldNotes}>
                <Input value={notes} onChange={(_, d) => setNotes(d.value)} disabled={mut.isPending} />
              </Field>

              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { reset(); onClose(); }} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {h.create}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export function HarvestPage() {
  const s = useStyles();
  const t = useT();
  const { fmtNum, fmtDayMonth } = useIntlFormat();
  const { formatCurrency, formatDate } = useLocaleSettings();
  const { loteName } = useEntityNames();
  const { user } = useAuthStore();
  // Roles con permiso harvest:write (ver DEFAULT_ROLE_PERMISSIONS en rbac.model.ts)
  const canWrite = user?.roles.some(r => ['FIELD_WORKER', 'SUPERVISOR', 'AGRONOMIST', 'ADMIN', 'SUPER_ADMIN'].includes(r)) ?? false;
  const [newHarvestOpen, setNewHarvestOpen] = useState(false);

  const { activeRanchoId } = useRanchoStore();
  // Filtro del árbol lote > zona > subzona: un lote incluye todo lo suyo; una zona, solo lo suyo.
  const [zoneFilter, setZoneFilter] = useState('');
  const { lotes: filterLotes } = useRanchoLotes(true, undefined, undefined, true);
  const { data: filterModules = [] } = useQuery({
    queryKey: ['modules-rancho', activeRanchoId],
    queryFn:  () => fetchModules({ ranchoId: activeRanchoId }),
  });
  const zoneOptions = useLoteZoneOptions(filterLotes, filterModules);
  const filterSel = zoneFilter ? parseLoteZoneId(zoneFilter, filterModules) : null;
  const filterModuleId = filterSel?.moduleId;

  const { data: summary, isLoading: loadingHarvest } = useQuery({
    queryKey: ['harvestSummary', activeRanchoId, filterModuleId],
    queryFn:  () => fetchHarvestSummary(activeRanchoId, filterModuleId),
  });

  const { data: mermaSummary, isLoading: loadingMerma } = useQuery({
    queryKey: ['mermaSummary', activeRanchoId, filterModuleId],
    queryFn:  () => fetchMermaSummary(activeRanchoId, filterModuleId),
  });

  if (loadingHarvest) return <Spinner size="small" label={t.harvest.loadingHarvests} />;
  if (!summary)       return null;

  // Un lote elegido (sin zona) se filtra aquí; la zona/subzona ya llega filtrada del servidor.
  const filterLoteId = filterSel && !filterSel.moduleId ? filterSel.loteId : undefined;
  if (filterLoteId) {
    summary.kpis    = summary.kpis.filter((k: HarvestKpi) => k.loteId === filterLoteId);
    summary.records = summary.records.filter((r: { loteId: string }) => r.loteId === filterLoteId);
  }

  // Los `?? 0` no son decorativos: un backend que no mande el campo (o lo mande
  // con otro nombre) producía `undefined`, y `undefined.toFixed()` reventaba la
  // página entera en cuanto había un solo lote cosechado.
  const totalKg    = summary.kpis.reduce((acc: number, k: HarvestKpi) => acc + (k.totalKg ?? 0), 0);
  const avgPackout = summary.kpis.length
    ? summary.kpis.reduce((acc: number, k: HarvestKpi) => acc + (k.avgPackout ?? 0), 0) / summary.kpis.length
    : 0;
  const avgCostKg  = summary.kpis.length
    ? summary.kpis.reduce((acc: number, k: HarvestKpi) => acc + (k.avgCostPerKg ?? 0), 0) / summary.kpis.length
    : 0;

  const kgByLoteChart = summary.kpis.map((k: HarvestKpi) => ({
    loteId:  loteName(k.loteId, k.loteName),
    totalKg: k.totalKg ?? 0,
    packout: Number((k.avgPackout ?? 0).toFixed(1)),
  }));

  const packoutChart = summary.records.slice(-20).map((r: { harvestedAt: string; gradeA?: number; gradeB?: number; gradeC?: number }) => ({
    fecha:  r.harvestedAt.slice(0, 10),
    gradeA: r.gradeA ?? 0,
    gradeB: r.gradeB ?? 0,
    gradeC: r.gradeC ?? 0,
  }));

  // ── Merma data
  const mermaRecords = (mermaSummary?.records ?? []).filter(r => !filterLoteId || r.loteId === filterLoteId);
  const mermaKpi     = filterLoteId && mermaSummary
    ? {
        ...mermaSummary.kpi,
        totalRejectedKg:      mermaRecords.reduce((a, r) => a + r.rejectedKg, 0),
        totalWasteImpactCost: mermaRecords.reduce((a, r) => a + (r.wasteImpactCost ?? 0), 0),
        recordCount:          mermaRecords.length,
        rejectedByReason:     mermaRecords.reduce<Record<string, number>>((a, r) => ({ ...a, [r.reason]: (a[r.reason] ?? 0) + r.rejectedKg }), {}),
      }
    : mermaSummary?.kpi;

  const mermaByReasonChart = mermaKpi
    ? Object.entries(mermaKpi.rejectedByReason).map(([reason, kg]) => ({
        razón: (t.harvest as Record<string, string>)[REASON_KEYS[reason as MermaReason]] ?? reason,
        kg: kg as number,
      }))
    : [];

  return (
    <div className={s.root}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <PageTitle>{t.harvest.title}</PageTitle>
        {canWrite && (
          <Button appearance="primary" onClick={() => setNewHarvestOpen(true)}>
            {t.harvest.newHarvestBtn}
          </Button>
        )}
      </div>

      <SearchableSelect
        label={t.harvest.fieldLoteZone}
        placeholder={t.common.searchLotePlaceholder}
        options={zoneOptions}
        value={zoneFilter}
        onChange={setZoneFilter}
        noResultsText={t.common.searchNoResults}
      />

      {/* ── KPIs de cosecha ─────────────────────────────────────────────────── */}
      <div className={s.kpiRow}>
        <KpiCard label={t.harvest.kpiTotalKg}  value={fmtNum(totalKg)} />
        <KpiCard
          label={t.harvest.kpiPackout}
          value={`${avgPackout.toFixed(1)}%`}
          sub={t.harvest.kpiPackoutSub}
          accent={avgPackout < 60}
        />
        <KpiCard
          label={t.harvest.kpiCostPerKg}
          value={formatCurrency(avgCostKg)}
          sub={t.harvest.kpiCostPerKgSub}
        />
        <KpiCard label={t.harvest.kpiCycles} value={fmtNum(summary.records.length)} />
      </div>

      {/* ── Gráficas de cosecha ──────────────────────────────────────────────── */}
      <div className={s.grid}>
        <div className={s.card}>
          <Text weight="semibold">{t.harvest.chartByLot}</Text>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={kgByLoteChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="loteId" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => `${fmtNum(v)} kg`} />
              <Bar dataKey="totalKg" fill={tokens.colorBrandBackground} name="Kg" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className={s.card}>
          <Text weight="semibold">{t.harvest.chartGradeBreakdown}</Text>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={packoutChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="fecha" tick={{ fontSize: 11 }} tickFormatter={fmtDayMonth} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => `${fmtNum(v)} kg`} />
              <Legend />
              <Bar dataKey="gradeA" stackId="a" fill="#1B7A32" name={t.harvest.chartGradeA} />
              <Bar dataKey="gradeB" stackId="a" fill="#E8A020" name={t.harvest.chartGradeB} />
              <Bar dataKey="gradeC" stackId="a" fill="#C0392B" name={t.harvest.chartGradeC} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Tabla historial cosecha ──────────────────────────────────────────── */}
      <div className={s.card}>
        <Text weight="semibold">{t.harvest.tableTitle}</Text>
        {summary.records.length === 0 ? (
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.harvest.noHarvests}</Text>
        ) : (
          <DataTable<HarvestRow>
            rows={summary.records as HarvestRow[]}
            rowKey={(r) => r.harvestId}
            columns={[
              { key: 'date', header: t.harvest.tableDate, render: (r) => formatDate(r.harvestedAt.slice(0, 10)) },
              { key: 'lot', header: t.harvest.tableLot, render: (r) => loteName(r.loteId) },
              { key: 'cycle', header: t.harvest.tableCycle, render: (r) => `#${r.cycleNumber}` },
              { key: 'kg', header: t.harvest.tableKg, align: 'right', render: (r) => <span style={{ fontWeight: 600 }}>{fmtNum(r.kgTotal)}</span> },
              { key: 'gradeA', header: t.harvest.tableGradeA, align: 'right', render: (r) => r.gradeA != null ? fmtNum(r.gradeA) : '—' },
              { key: 'packout', header: t.harvest.tablePackout, align: 'right', render: (r) => r.packoutPct != null ? (
                <Badge appearance="tint" size="small" color={r.packoutPct >= 70 ? 'success' : r.packoutPct >= 50 ? 'warning' : 'danger'}>
                  {r.packoutPct.toFixed(1)}%
                </Badge>
              ) : '—' },
              { key: 'cost', header: t.harvest.tableCostPerKg, align: 'right', render: (r) => r.costPerKg != null ? formatCurrency(r.costPerKg) : '—' },
              { key: 'by', header: t.harvest.tableRecordedBy, render: (r) => <span style={{ color: tokens.colorNeutralForeground3 }}>{r.recordedByName ?? r.recordedBy}</span> },
            ]}
          />
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          SECCIÓN MERMA — Flujo 12
      ══════════════════════════════════════════════════════════════════════ */}

      <div className={s.sectionHeader}>
        <Text size={500} weight="semibold">🗑️ {t.harvest.mermaTitle}</Text>
      </div>

      {/* KPIs de merma */}
      {loadingMerma ? (
        <Spinner size="small" label={t.harvest.mermaLoading} />
      ) : (
        <>
          <div className={s.kpiRow}>
            <KpiCard
              label={t.harvest.mermaKpiRejectedKg}
              value={fmtNum((mermaKpi?.totalRejectedKg ?? 0))}
              accent={(mermaKpi?.totalRejectedKg ?? 0) > 100}
            />
            <KpiCard
              label={t.harvest.mermaKpiImpact}
              value={mermaKpi?.totalWasteImpactCost
                ? formatCurrency(mermaKpi.totalWasteImpactCost)
                : '—'}
            />
            <KpiCard
              label={t.harvest.mermaKpiRecords}
              value={(mermaKpi?.recordCount ?? 0).toString()}
            />
          </div>

          {/* Gráfica merma por razón */}
          {mermaByReasonChart.length > 0 && (
            <div className={s.grid}>
              <div className={s.card}>
                <Text weight="semibold">{t.harvest.mermaChartTitle}</Text>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={mermaByReasonChart} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 11 }} unit=" kg" />
                    <YAxis type="category" dataKey="razón" tick={{ fontSize: 11 }} width={140} />
                    <Tooltip formatter={(v: number) => `${fmtNum(v)} kg`} />
                    <Bar dataKey="kg" fill="#C0392B" name="kg" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* placeholder para futuro gráfico (2ª columna) */}
              <div />
            </div>
          )}

          {/* Tabla historial merma */}
          <div className={s.card}>
            <Text weight="semibold">{t.harvest.mermaTitle}</Text>
            {mermaRecords.length === 0 ? (
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                {t.harvest.mermaNoRecords}
              </Text>
            ) : (
              <DataTable<(typeof mermaRecords)[number]>
                rows={mermaRecords}
                rowKey={(r) => r.mermaId}
                columns={[
                  { key: 'date', header: t.harvest.mermaTableDate, render: (r) => formatDate(r.registeredAt.slice(0, 10)) },
                  { key: 'reason', header: t.harvest.mermaTableReason, render: (r) => (t.harvest as Record<string, string>)[REASON_KEYS[r.reason]] ?? r.reason },
                  { key: 'kg', header: t.harvest.mermaTableKg, align: 'right', render: (r) => (
                    <span style={{ fontWeight: 600, color: tokens.colorPaletteRedForeground1 }}>{fmtNum(r.rejectedKg ?? 0)}</span>
                  ) },
                  { key: 'boxes', header: t.harvest.mermaTableBoxes, align: 'right', render: (r) => r.rejectedBoxes != null ? r.rejectedBoxes : '—' },
                  { key: 'label', header: t.harvest.mermaTableLabel, render: (r) => (
                    <span style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>{r.etiquetaId ?? '—'}</span>
                  ) },
                  { key: 'impact', header: t.harvest.mermaTableImpact, align: 'right', render: (r) => r.wasteImpactCost != null ? formatCurrency(r.wasteImpactCost) : '—' },
                  { key: 'status', header: t.harvest.mermaTableStatus, render: (r) => (
                    <Badge appearance="tint" size="small" color={r.status === 'CERRADA' ? 'success' : r.status === 'VERIFICADA' ? 'warning' : 'informative'}>
                      {(t.merma as Record<string, string>)[STATUS_KEYS[r.status]] ?? r.status}
                    </Badge>
                  ) },
                ]}
              />
            )}
          </div>
        </>
      )}

      <NewHarvestDialog open={newHarvestOpen} onClose={() => setNewHarvestOpen(false)} />
    </div>
  );
}
